import React, { useState, useEffect } from 'react';
import { Competition, Season } from '../../types/sports';

interface CompetitionFormProps {
  competition?: Competition;
  seasons: Season[];
  onSave: (data: {
    name: string;
    shortName?: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    seasonId: number;
    isActive?: boolean;
  }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const CompetitionForm: React.FC<CompetitionFormProps> = ({
  competition,
  seasons,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    shortName: '',
    description: '',
    startDate: '',
    endDate: '',
    seasonId: 0,
    isActive: true
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (competition) {
      setFormData({
        name: competition.name || '',
        shortName: competition.shortName || '',
        description: competition.description || '',
        startDate: competition.startDate ? new Date(competition.startDate).toISOString().split('T')[0] : '',
        endDate: competition.endDate ? new Date(competition.endDate).toISOString().split('T')[0] : '',
        seasonId: competition.seasonId || 0,
        isActive: competition.isActive !== undefined ? competition.isActive : true
      });
    }
  }, [competition]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'seasonId' ? parseInt(value) : value)
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (formData.seasonId === 0) {
      newErrors.seasonId = 'Saison ist erforderlich';
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (startDate >= endDate) {
        newErrors.endDate = 'Enddatum muss nach dem Startdatum liegen';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        name: formData.name.trim(),
        shortName: formData.shortName.trim() || undefined,
        description: formData.description.trim() || undefined,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        seasonId: formData.seasonId,
        isActive: formData.isActive
      };
      onSave(submitData);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>
            {competition ? 'Wettbewerb bearbeiten' : 'Neuer Wettbewerb'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              style={{
                ...inputStyle,
                ...(errors.name ? errorInputStyle : {})
              }}
              placeholder="Name des Wettbewerbs"
            />
            {errors.name && <span style={errorTextStyle}>{errors.name}</span>}
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Kurzname</label>
            <input
              type="text"
              name="shortName"
              value={formData.shortName}
              onChange={handleChange}
              disabled={loading}
              style={inputStyle}
              placeholder="Kurzname (optional)"
              maxLength={20}
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Beschreibung</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={loading}
              style={textareaStyle}
              placeholder="Beschreibung des Wettbewerbs (optional)"
              rows={3}
            />
          </div>

          <div style={formRowStyle}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Startdatum</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                disabled={loading}
                style={inputStyle}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Enddatum</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                disabled={loading}
                style={{
                  ...inputStyle,
                  ...(errors.endDate ? errorInputStyle : {})
                }}
              />
              {errors.endDate && <span style={errorTextStyle}>{errors.endDate}</span>}
            </div>
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>Saison *</label>
            <select
              name="seasonId"
              value={formData.seasonId}
              onChange={handleChange}
              disabled={loading}
              style={{
                ...selectStyle,
                ...(errors.seasonId ? errorInputStyle : {})
              }}
            >
              <option value={0}>Saison auswählen</option>
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.name} ({season.league?.name})
                </option>
              ))}
            </select>
            {errors.seasonId && <span style={errorTextStyle}>{errors.seasonId}</span>}
          </div>

          {competition && (
            <div style={formGroupStyle}>
              <label style={labelStyle}>Status</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ margin: 0 }}
                />
                <label htmlFor="isActive" style={{ margin: 0, fontSize: '0.9rem' }}>
                  Aktiv
                </label>
              </div>
            </div>
          )}

          <div style={formActionsStyle}>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              style={cancelButtonStyle}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              style={submitButtonStyle}
            >
              {loading ? 'Speichern...' : (competition ? 'Aktualisieren' : 'Erstellen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles
const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '2rem',
  width: '90%',
  maxWidth: '600px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

const headerStyle: React.CSSProperties = {
  marginBottom: '1.5rem',
  borderBottom: '1px solid #e5e7eb',
  paddingBottom: '1rem',
};

const titleStyle: React.CSSProperties = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#1f2937',
  margin: 0,
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const formGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
};

const formRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
};

const inputStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '1rem',
  outline: 'none',
  transition: 'border-color 0.2s',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: '80px',
};

const errorInputStyle: React.CSSProperties = {
  borderColor: '#ef4444',
};

const errorTextStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: '#ef4444',
  marginTop: '0.25rem',
};

const formActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '1rem',
  marginTop: '1.5rem',
  paddingTop: '1rem',
  borderTop: '1px solid #e5e7eb',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  backgroundColor: 'white',
  color: '#374151',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

const submitButtonStyle: React.CSSProperties = {
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: '#3b82f6',
  color: 'white',
  fontSize: '1rem',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
};

export default CompetitionForm;
